import { DateTime } from "luxon";
import { markLocalActivity } from "../../lib/activity-alerts";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ApiError,
  buildBookingAdmitQrPayload,
  providerApi,
  type BookingStatus,
  type CreateProviderBookingInput,
  type ProviderBooking,
  type ProviderVenue,
  type UpdateProviderBookingInput,
  isOfflineApiError,
} from "../../lib/api";
import { calculateIntervalTotalAmount } from "../../lib/provider-pricing";
import {
  createOfflineRecordId,
  writeCachedApiResponse,
} from "../../lib/offline-store";
import {
  subscribeRealtimeConnection,
  subscribeRealtimeVersion,
} from "../../lib/realtime";
import { useProviderOffline } from "../../lib/provider-offline";
import { useAppPreferences } from "../../lib/preferences";
import { formatDateTimeForPicker } from "./booking-utils";

function getErrorDetail(error: unknown, fallback: string) {
  if (error instanceof ApiError) {
    return error.detail;
  }

  return fallback;
}

interface UseProviderBookingsArgs {
  scheduledFromLocal: string | null;
  scheduledToLocal: string | null;
  selectedVenue: ProviderVenue | null;
}

function buildBookingsPath({
  scheduledFromLocal,
  scheduledToLocal,
  selectedVenue,
}: UseProviderBookingsArgs) {
  const params = new URLSearchParams();

  if (selectedVenue?.id) {
    params.set("venue_id", selectedVenue.id);
  }

  if (scheduledFromLocal) {
    params.set("scheduled_from_local", scheduledFromLocal);
  }

  if (scheduledToLocal) {
    params.set("scheduled_to_local", scheduledToLocal);
  }

  const queryString = params.toString();
  return `/api/v1/provider/bookings${queryString ? `?${queryString}` : ""}`;
}

function isBookingVisible(
  booking: ProviderBooking,
  {
    scheduledFromLocal,
    scheduledToLocal,
    selectedVenue,
  }: UseProviderBookingsArgs,
) {
  if (booking.venue_id !== selectedVenue?.id) return false;
  const startLocal = formatDateTimeForPicker(
    booking.scheduled_start_at,
    booking.venue_timezone,
  );
  const endLocal = formatDateTimeForPicker(
    booking.scheduled_end_at,
    booking.venue_timezone,
  );

  if (scheduledFromLocal && endLocal < scheduledFromLocal) {
    return false;
  }

  if (scheduledToLocal && startLocal > scheduledToLocal) {
    return false;
  }

  return true;
}

function sortBookings(bookings: ProviderBooking[]) {
  return [...bookings].sort((left, right) =>
    left.scheduled_start_at.localeCompare(right.scheduled_start_at),
  );
}

function toUtcIso(localValue: string, timezone: string) {
  return (
    DateTime.fromISO(localValue, { zone: timezone }).toUTC().toISO() ??
    new Date().toISOString()
  );
}

export function useProviderBookings({
  scheduledFromLocal,
  scheduledToLocal,
  selectedVenue,
}: UseProviderBookingsArgs) {
  const { t } = useAppPreferences();
  const { queueMutation, syncVersion } = useProviderOffline();
  const [loadedBookings, setBookings] = useState<ProviderBooking[]>([]);
  const [loadedScope, setLoadedScope] = useState<string | null>(null);
  const requestRevision = useRef(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [savingBookingId, setSavingBookingId] = useState<string | null>(null);
  const [updatingStatusId, setUpdatingStatusId] = useState<string | null>(null);
  const cachePath = useMemo(
    () =>
      buildBookingsPath({
        scheduledFromLocal,
        scheduledToLocal,
        selectedVenue,
      }),
    [
      scheduledFromLocal,
      scheduledToLocal,
      selectedVenue,
    ],
  );

  const currentScope = useRef(cachePath);
  currentScope.current = cachePath;
  const bookings = loadedScope === cachePath ? loadedBookings : [];
  useEffect(() => () => {
    requestRevision.current += 1;
  }, []);

  const persistBookings = useCallback(
    (nextBookings: ProviderBooking[]) => {
      const sortedBookings = sortBookings(nextBookings);
      if (currentScope.current === cachePath) {
        setBookings(sortedBookings);
        setLoadedScope(cachePath);
      }
      writeCachedApiResponse(cachePath, {
        bookings: sortedBookings,
      });
    },
    [cachePath],
  );

  const reload = useCallback(async () => {
    // An old mutation completion may still hold a reload for another venue.
    if (currentScope.current !== cachePath) return;
    const revision = ++requestRevision.current;
    const isCurrent = () =>
      revision === requestRevision.current && currentScope.current === cachePath;
    if (!selectedVenue) {
      setBookings([]);
      setLoadedScope(cachePath);
      setError(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await providerApi.listBookings({
        venueId: selectedVenue.id,
        scheduledFromLocal: scheduledFromLocal ?? undefined,
        scheduledToLocal: scheduledToLocal ?? undefined,
      });
      if (!isCurrent()) return;
      setBookings(sortBookings(response.bookings));
      setLoadedScope(cachePath);
    } catch (requestError) {
      if (isCurrent()) {
        setError(getErrorDetail(requestError, t("Unable to load bookings.")));
      }
    } finally {
      if (isCurrent()) setIsLoading(false);
    }
  }, [
    cachePath,
    scheduledFromLocal,
    scheduledToLocal,
    selectedVenue,
    t,
  ]);

  useEffect(() => {
    setNotice(null);
    void reload();
  }, [reload]);

  useEffect(() => {
    if (syncVersion === 0) {
      return;
    }

    void reload();
  }, [reload, syncVersion]);

  // Reload when realtime push arrives.
  useEffect(() => {
    return subscribeRealtimeVersion(() => {
      void reload();
    });
  }, [reload]);

  useEffect(() => {
    return subscribeRealtimeConnection((isConnected) => {
      if (isConnected) {
        void reload();
      }
    });
  }, [reload]);

  // Reconcile periodically even with an open SSE stream.
  useEffect(() => {
    if (!selectedVenue) {
      return;
    }

    const intervalId = setInterval(() => {
      void reload();
    }, 30000);

    return () => clearInterval(intervalId);
  }, [reload, selectedVenue]);

  const createBooking = useCallback(
    async (payload: CreateProviderBookingInput) => {
      setCreating(true);
      setError(null);
      setNotice(null);

      try {
        const booking = await providerApi.createBooking(payload);
        markLocalActivity(booking.id);
        await reload();
        setNotice(t("Created booking for {name}.", { name: booking.customer_name }));
        return booking;
      } catch (requestError) {
        if (isOfflineApiError(requestError) && selectedVenue) {
          const unit = selectedVenue.units.find((value) => value.id === payload.unit_id);
          const offlineBookingId = createOfflineRecordId("booking");
          const scheduledStartAt = toUtcIso(
            payload.scheduled_start_local,
            selectedVenue.timezone,
          );
          const scheduledEndAt = toUtcIso(payload.scheduled_end_local, selectedVenue.timezone);
          const booking: ProviderBooking = {
            id: offlineBookingId,
            venue_id: selectedVenue.id,
            venue_name: selectedVenue.name,
            venue_timezone: selectedVenue.timezone,
            unit_id: payload.unit_id,
            unit_name: unit?.name ?? t("Unit"),
            customer_name: payload.customer_name,
            customer_email: payload.customer_email ?? null,
            customer_phone: payload.customer_phone ?? null,
            type: "fixed_time",
            status: "pending",
            scheduled_start_at: scheduledStartAt,
            scheduled_end_at: scheduledEndAt,
            scheduled_total_amount: unit
              ? calculateIntervalTotalAmount(
                  unit,
                  selectedVenue.timezone,
                  scheduledStartAt,
                  scheduledEndAt,
                )
              : 0,
            notes: payload.notes ?? null,
            status_note: null,
            admit_qr_payload: buildBookingAdmitQrPayload(offlineBookingId),
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          };
          if (isBookingVisible(booking, {
            scheduledFromLocal,
            scheduledToLocal,
            selectedVenue,
          })) {
            persistBookings([...bookings, booking]);
          }
          queueMutation({
            method: "POST",
            path: "/api/v1/provider/bookings",
            body: payload,
            label: `Create booking for ${payload.customer_name}`,
            local_resource_id: booking.id,
          });
          setNotice(
            t("Saved booking for {name} offline. It will sync when connection returns.", { name: payload.customer_name }),
          );
          return booking;
        }

        setError(getErrorDetail(requestError, t("Unable to create booking.")));
        return null;
      } finally {
        setCreating(false);
      }
    },
    [
      bookings,
      persistBookings,
      queueMutation,
      reload,
      scheduledFromLocal,
      scheduledToLocal,
      selectedVenue,
      t,
    ],
  );

  const updateBooking = useCallback(
    async (bookingId: string, payload: UpdateProviderBookingInput) => {
      setSavingBookingId(bookingId);
      setError(null);
      setNotice(null);

      try {
        const booking = await providerApi.updateBooking(bookingId, payload);
        await reload();
        setNotice(t("Updated booking for {name}.", { name: booking.customer_name }));
        return booking;
      } catch (requestError) {
        if (isOfflineApiError(requestError) && selectedVenue) {
          const currentBooking = bookings.find((value) => value.id === bookingId) ?? null;
          if (!currentBooking) {
            setError(t("This booking is not cached for offline editing."));
            return null;
          }

          const unit = selectedVenue.units.find((value) => value.id === payload.unit_id);
          const scheduledStartAt = toUtcIso(
            payload.scheduled_start_local,
            selectedVenue.timezone,
          );
          const scheduledEndAt = toUtcIso(payload.scheduled_end_local, selectedVenue.timezone);
          const updatedBooking: ProviderBooking = {
            ...currentBooking,
            unit_id: payload.unit_id,
            unit_name: unit?.name ?? currentBooking.unit_name,
            customer_name: payload.customer_name,
            customer_email: payload.customer_email ?? null,
            customer_phone: payload.customer_phone ?? null,
            scheduled_start_at: scheduledStartAt,
            scheduled_end_at: scheduledEndAt,
            scheduled_total_amount: unit
              ? calculateIntervalTotalAmount(
                  unit,
                  selectedVenue.timezone,
                  scheduledStartAt,
                  scheduledEndAt,
                )
              : currentBooking.scheduled_total_amount,
            notes: payload.notes ?? null,
            updated_at: new Date().toISOString(),
          };
          const nextBookings = bookings
            .filter((value) => value.id !== bookingId)
            .concat(
              isBookingVisible(updatedBooking, {
                scheduledFromLocal,
                scheduledToLocal,
                selectedVenue,
              })
                ? [updatedBooking]
                : [],
            );
          persistBookings(nextBookings);
          queueMutation({
            method: "PUT",
            path: `/api/v1/provider/bookings/${bookingId}`,
            body: payload,
            label: `Update booking for ${payload.customer_name}`,
          });
          setNotice(
            t("Saved booking for {name} offline. It will sync when connection returns.", { name: payload.customer_name }),
          );
          return updatedBooking;
        }

        setError(getErrorDetail(requestError, t("Unable to update booking.")));
        return null;
      } finally {
        setSavingBookingId(null);
      }
    },
    [
      bookings,
      persistBookings,
      queueMutation,
      reload,
      scheduledFromLocal,
      scheduledToLocal,
      selectedVenue,
      t,
    ],
  );

  const updateBookingStatus = useCallback(
    async (bookingId: string, status: BookingStatus, note?: string | null) => {
      setUpdatingStatusId(bookingId);
      setError(null);
      setNotice(null);

      try {
        const booking = await providerApi.updateBookingStatus(bookingId, {
          status,
          note,
        });
        await reload();
        setNotice(
          t("Marked booking for {name} as {status}.", { name: booking.customer_name, status: t(status.replaceAll("_", " ")) }),
        );
        return booking;
      } catch (requestError) {
        if (isOfflineApiError(requestError) && selectedVenue) {
          const currentBooking = bookings.find((value) => value.id === bookingId) ?? null;
          if (!currentBooking) {
            setError(t("This booking is not cached for offline status changes."));
            return null;
          }

          const updatedBooking: ProviderBooking = {
            ...currentBooking,
            status,
            status_note: note ?? null,
            updated_at: new Date().toISOString(),
          };
          const nextBookings = bookings
            .filter((value) => value.id !== bookingId)
            .concat(
              isBookingVisible(updatedBooking, {
                scheduledFromLocal,
                scheduledToLocal,
                selectedVenue,
              })
                ? [updatedBooking]
                : [],
            );
          persistBookings(nextBookings);
          queueMutation({
            method: "PUT",
            path: `/api/v1/provider/bookings/${bookingId}/status`,
            body: {
              status,
              note: note ?? null,
            },
            label: `Mark booking ${status.replaceAll("_", " ")}`,
          });
          setNotice(
            t("Saved booking status for {name} offline. It will sync when connection returns.", { name: currentBooking.customer_name }),
          );
          return updatedBooking;
        }

        setError(
          getErrorDetail(requestError, t("Unable to update booking status.")),
        );
        return null;
      } finally {
        setUpdatingStatusId(null);
      }
    },
    [
      bookings,
      persistBookings,
      queueMutation,
      reload,
      scheduledFromLocal,
      scheduledToLocal,
      selectedVenue,
      t,
    ],
  );

  return {
    bookings,
    isLoading,
    error,
    notice,
    creating,
    savingBookingId,
    updatingStatusId,
    createBooking,
    updateBooking,
    updateBookingStatus,
    reload,
  };
}
